/**
 * Carbon line tabs (DESIGN.md "Navigation"): 48px items over a 2px
 * border-subtle baseline; the selected item is 600 with a 2px
 * interactive-line underline. Page-level sub-navigation (Security Center,
 * the pillar pages, the Command Center views).
 *
 * Selection is explicit (`value`), never derived from the router: NavLink
 * matching ignores the search params, so every `/security?tab=…` link would
 * read as current (blueprint §4.5). Two modes:
 *   links  every item has `to`: a <nav> of router links; only the selected
 *          one carries aria-current="page".
 *   tabs   otherwise: role="tablist" buttons with aria-selected, a roving
 *          tabindex and arrow-key selection. Tab ids are `${tabIdPrefix}${id}`
 *          and point at `${panelIdPrefix}${id}`, the pages' existing panels.
 *
 * The strip is its own horizontal scroller (no scrollbar), so five tabs with
 * count tags never widen <main> on a phone; the selected tab is scrolled into
 * the strip's view. `actions` sits beside the strip and wraps to its own row
 * below 672px.
 */
import { useEffect, useRef, type KeyboardEvent, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Tag } from './Tag'
import type { LineTabItem, LineTabsProps } from './types'

const ITEM =
  'inline-flex h-12 flex-none items-center gap-2 whitespace-nowrap border-b-2 px-4 text-body ' +
  'transition-colors duration-110 ease-carbon'
// Each state sets its own border color (Tailwind emits border-transparent after the token colors).
const SELECTED = 'border-interactive-line font-semibold text-text-primary'
const IDLE = 'border-transparent font-normal text-text-secondary hover:border-line-strong hover:text-text-primary'

const hasCount = (c: LineTabItem['count']) => c !== null && c !== undefined && c !== ''

function Label({ item }: { item: LineTabItem }) {
  return (
    <>
      {item.label}
      {hasCount(item.count) ? (
        <Tag size="sm" className="tabular-nums">
          {String(item.count)}
        </Tag>
      ) : null}
    </>
  )
}

const nameOf = (item: LineTabItem) => (hasCount(item.count) ? `${item.label}, ${item.count}` : undefined)

export function LineTabs({
  items,
  value,
  onChange,
  actions,
  'aria-label': ariaLabel,
  tabIdPrefix = 'tab-',
  panelIdPrefix = 'panel-',
  className,
}: LineTabsProps) {
  const stripRef = useRef<HTMLElement>(null)
  const linkMode = items.length > 0 && items.every(i => i.to)

  // Keep the selected tab inside the strip's own scroll box. scrollLeft only,
  // so <main> never scrolls vertically because of it.
  useEffect(() => {
    const strip = stripRef.current
    const el = strip?.querySelector<HTMLElement>('[data-selected="true"]')
    if (!strip || !el) return
    // The strip is position:relative, so it is the item's offsetParent.
    const left = el.offsetLeft
    const right = left + el.offsetWidth
    if (left < strip.scrollLeft) strip.scrollLeft = left
    else if (right > strip.scrollLeft + strip.clientWidth) strip.scrollLeft = right - strip.clientWidth
  }, [value])

  const enabled = items.filter(i => !i.disabled)
  // Tab mode keeps one tab in the tab order even when `value` names no enabled tab (as ContainedTabs does).
  const tabStop = enabled.some(i => i.id === value) ? value : enabled[0]?.id
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = enabled.findIndex(i => i.id === tabStop)
    const next =
      e.key === 'ArrowRight' ? enabled[(at + 1) % enabled.length]
        : e.key === 'ArrowLeft' ? enabled[(at - 1 + enabled.length) % enabled.length]
          : e.key === 'Home' ? enabled[0]
            : e.key === 'End' ? enabled[enabled.length - 1]
              : undefined
    if (!next) return
    e.preventDefault()
    onChange?.(next.id)
    stripRef.current?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(next.id)}"]`)?.focus()
  }

  const STRIP = 'relative flex min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'

  const strip = linkMode ? (
    <nav ref={stripRef} aria-label={ariaLabel} className={STRIP}>
      {items.map(item => {
        const selected = item.id === value
        return (
          <Link
            key={item.id}
            to={item.to as string}
            data-selected={selected || undefined}
            aria-current={selected ? 'page' : undefined}
            aria-label={nameOf(item)}
            onClick={() => onChange?.(item.id)}
            className={clsx(ITEM, selected ? SELECTED : IDLE)}
          >
            <Label item={item} />
          </Link>
        )
      })}
    </nav>
  ) : (
    <div ref={stripRef as RefObject<HTMLDivElement>} role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown} className={STRIP}>
      {items.map(item => {
        const selected = item.id === value
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${tabIdPrefix}${item.id}`}
            aria-controls={`${panelIdPrefix}${item.id}`}
            aria-selected={selected}
            aria-label={nameOf(item)}
            tabIndex={item.id === tabStop ? 0 : -1}
            disabled={item.disabled}
            data-tab-id={item.id}
            data-selected={selected || undefined}
            onClick={() => onChange?.(item.id)}
            className={clsx(ITEM, selected ? SELECTED : IDLE, 'disabled:cursor-not-allowed disabled:text-text-placeholder')}
          >
            <Label item={item} />
          </button>
        )
      })}
    </div>
  )

  return (
    <div className={clsx('flex min-w-0 flex-col c-md:flex-row c-md:items-center', className)}>
      <div className="relative min-w-0 flex-1">
        {/* The 2px baseline the items' underlines sit on. */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-line-subtle" />
        {strip}
      </div>
      {actions ? (
        <div className="flex min-w-0 flex-wrap items-center gap-3 pt-3 c-md:flex-none c-md:pl-4 c-md:pt-0">{actions}</div>
      ) : null}
    </div>
  )
}

export default LineTabs
